import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "auth/Auth";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import { Alert, MenuItem, Snackbar, TextField } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";

import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import StatusBadge from "components/StatusBadge";
import DataLoadingSpinner from "components/Spinners/DataLoadingSpinner";
import { commonIconButtonStyles, commonIconStyles } from "assets/theme/components/iconButtonStyles";
import { previewPrivateObject } from "utils/S3";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import {
    TravelDetailFieldGrid,
    buildTravelAuditFields,
} from "components/travel/TravelDetailSection";

/** early_bird → Early Bird, mid_range → Mid Range */
function formatEnumLabel(value) {
    if (value === null || value === undefined || value === "") return value;
    if (typeof value !== "string") return value;
    return value
        .replace(/[_-]+/g, " ")
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function toTitleCase(str) {
    return formatEnumLabel(str) || "";
}

const formatEntityId = (prefix, id) => {
    if (id === null || id === undefined) return "-";
    return id < 9999 ? `${prefix}-${String(id).padStart(4, "0")}` : `${prefix}-${id}`;
};

const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const formatDateRange = (start, end) => `${formatDate(start)} - ${formatDate(end)}`;

const formatMoney = (value, currency) => {
    if (value === null || value === undefined) return "-";
    const amount = Number(value).toLocaleString();
    return currency ? `${amount} ${currency}` : amount;
};

const headerChipSx = {
    bgcolor: "rgba(255,255,255,0.18)",
    color: "#ffffff",
    border: "1px solid rgba(255,255,255,0.35)",
    height: 24,
    "& .MuiChip-label": {
        color: "#ffffff",
        fontWeight: 500,
    },
};

const DetailSection = ({
    title,
    children,
    mb = 3,
    headerAction = null,
    collapsible = false,
    defaultExpanded = true,
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    return (
        <MDBox
            mb={mb}
            p={3}
            borderRadius="lg"
            sx={{
                backgroundColor: "#ffffff",
                boxShadow: 3,
            }}
        >
            {title ? (
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={2}
                    mb={expanded ? 2 : 0}
                    sx={collapsible ? { cursor: "pointer", userSelect: "none" } : {}}
                    onClick={collapsible ? () => setExpanded((prev) => !prev) : undefined}
                >
                    <MDTypography variant="h6" fontWeight="medium" color="dark">
                        {title}
                    </MDTypography>
                    <MDBox display="flex" alignItems="center" gap={1}>
                        {headerAction}
                        {collapsible && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setExpanded((prev) => !prev);
                                }}
                                aria-label={expanded ? "Collapse section" : "Expand section"}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "grey.300",
                                    borderRadius: 1,
                                    p: 0.5,
                                    width: 28,
                                    height: 28,
                                }}
                            >
                                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        )}
                    </MDBox>
                </MDBox>
            ) : null}

            {collapsible ? (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    {children}
                </Collapse>
            ) : (
                children
            )}
        </MDBox>
    );
};

const EmptyState = ({ message, icon = null }) => (
    <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={3}
        px={2}
        textAlign="center"
    >
        {icon}
        <MDTypography variant="body2" color="text" mt={icon ? 1 : 0}>
            {message}
        </MDTypography>
    </MDBox>
);

const TripPackageDetail = () => {
    const { packageId } = useParams();
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();

    const [pkg, setPkg] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

    const [heroImageUrl, setHeroImageUrl] = useState("");
    const [galleryItems, setGalleryItems] = useState([]);
    const [isMediaLoading, setIsMediaLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [isCloning, setIsCloning] = useState(false);
    const [websiteSite, setWebsiteSite] = useState(null);
    const [websiteSites, setWebsiteSites] = useState([]);
    const [websitePublication, setWebsitePublication] = useState(null);
    const [websiteLoading, setWebsiteLoading] = useState(false);

    const findCatalogueEntryForPackage = async (siteId) => {
        const limit = 100;
        let offset = 0;
        const targetId = Number(packageId);
        while (true) {
            const catalogueResponse = await axiosInstance.get(`/api/v1/websites/sites/${siteId}/catalogue`, {
                params: { offset, limit },
            });
            const rows = catalogueResponse.data || [];
            const match = rows.find((entry) => Number(entry.trip_package_id) === targetId);
            if (match) return match;
            if (rows.length < limit) return null;
            offset += limit;
        }
    };

    const loadWebsitePublication = async () => {
        try {
            const sitesResponse = await axiosInstance.get("/api/v1/websites/sites");
            const configuredSites = sitesResponse.data || [];
            const configuredSite = configuredSites[0] || null;
            setWebsiteSites(configuredSites);
            setWebsiteSite(configuredSite);
            if (!configuredSite) return;
            setWebsitePublication(await findCatalogueEntryForPackage(configuredSite.id));
        } catch (websiteError) {
            // A user without Website access can still use the Trip Package page.
            setWebsiteSite(null);
            setWebsitePublication(null);
        }
    };

    const selectWebsiteSite = async (siteId) => {
        const selectedSite = websiteSites.find((candidate) => candidate.id === siteId) || null;
        setWebsiteSite(selectedSite);
        setWebsitePublication(null);
        if (!selectedSite) return;
        try {
            setWebsitePublication(await findCatalogueEntryForPackage(selectedSite.id));
        } catch (websiteError) {
            setSnackbar({ open: true, message: "Website publication could not be loaded.", severity: "error" });
        }
    };

    const setWebsitePublished = async (isPublished) => {
        if (!websiteSite || !pkg || websiteLoading) return;
        setWebsiteLoading(true);
        try {
            const response = await axiosInstance.put(`/api/v1/websites/sites/${websiteSite.id}/catalogue/${pkg.id}`, {
                trip_package_id: pkg.id,
                is_published: isPublished,
                public_slug: websitePublication?.public_slug || null,
            });
            setWebsitePublication(response.data);
            setSnackbar({ open: true, message: isPublished ? "Package published to the website." : "Package removed from the website.", severity: "success" });
        } catch (websiteError) {
            setSnackbar({ open: true, message: websiteError.response?.data?.detail?.message || websiteError.response?.data?.detail || "Website publication could not be updated.", severity: "error" });
        } finally {
            setWebsiteLoading(false);
        }
    };

    const isWebsitePublished = Boolean(websitePublication?.is_published);

    const handleDeletePackage = async () => {
        if (!pkg || isDeleting) return;
        setIsDeleting(true);
        try {
            await axiosInstance.delete(`/packages/${pkg.id}`);
            setSnackbar({
                open: true,
                message: "Package deleted successfully.",
                severity: "success",
            });
            setTimeout(() => {
                navigate("/trip-packages");
            }, 800);
        } catch (deleteError) {
            const errorMessage = deleteError.response?.data?.detail
                || deleteError.response?.data?.message
                || deleteError.message
                || "Unable to delete package.";
            setSnackbar({ open: true, message: errorMessage, severity: "error" });
            throw deleteError;
        } finally {
            setIsDeleting(false);
        }
    };

    const handleClonePackage = async () => {
        if (!pkg || isCloning) return;
        setIsCloning(true);
        try {
            // Deep-clone as draft (country cleared), then open detail page in edit mode
            // — same UI as Edit Destination, not the step wizard.
            const res = await axiosInstance.post(`/packages/${pkg.id}/clone`);
            const newId = res.data?.id;
            if (!newId) {
                throw new Error(res.data?.message || 'Clone did not return a destination id.');
            }
            navigate(`/trip-packages/detail/${newId}?fromClone=1`);
        } catch (cloneError) {
            const errorMessage = cloneError.response?.data?.detail
                || cloneError.response?.data?.message
                || cloneError.message
                || 'Unable to clone destination.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        } finally {
            setIsCloning(false);
        }
    };


    const resolvePackageMedia = async (packageData) => {
        setIsMediaLoading(true);
        try {
            const attachmentsResponse = await axiosInstance.get("/attachments/", {
                params: { parent_table: "trip_packages", parent_id: packageData.id },
            });
            const imageAttachments = (attachmentsResponse.data || []).filter(
                (a) => a.file_type && a.file_type.startsWith("image/")
            );
            const heroAttachment = imageAttachments.length
                ? [...imageAttachments].sort((a, b) => b.id - a.id)[0]
                : null;

            if (heroAttachment?.bucket_name && heroAttachment?.storage_path) {
                const url = await previewPrivateObject({ axiosInstance, attachmentId: heroAttachment.id });
                setHeroImageUrl(url || "");
            } else {
                setHeroImageUrl("");
            }

            const resolvedGallery = await Promise.all(
                (packageData.image_gallery || []).map(async (item) => {
                    let image = item.image || "";
                    if (item.image_attachment_id) {
                        try {
                            image =
                                (await previewPrivateObject({
                                    axiosInstance,
                                    attachmentId: item.image_attachment_id,
                                })) || image;
                        } catch (previewError) {
                            console.error("Error previewing gallery attachment:", previewError);
                        }
                    }
                    return { ...item, image };
                })
            );
            setGalleryItems(resolvedGallery);
        } catch (error) {
            console.error("Error loading package media:", error);
        } finally {
            setIsMediaLoading(false);
        }
    };

    useEffect(() => {
        fetchPackage();
        loadWebsitePublication();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packageId]);

    const fetchPackage = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/trip-packages/${packageId}`);
            setPkg(response.data);
            await resolvePackageMedia(response.data);
        } catch (error) {
            console.error("Error fetching package detail:", error);
            setSnackbar({ open: true, message: "Unable to load package details.", severity: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditPackage = () => {
        if (!pkg) return;
        const primaryTripId = pkg.trips?.[0]?.id;
        if (!primaryTripId) {
            setSnackbar({
                open: true,
                message: "This package isn't linked to any trip yet. Link it to a trip before editing.",
                severity: "warning",
            });
            return;
        }
        navigate(`/trips/${primaryTripId}/packages/${pkg.id}/edit?step=packages`);
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <DataLoadingSpinner
                    entityType="default"
                    title="Loading Package Data"
                    message="Please wait while we fetch the package information..."
                />
            </DashboardLayout>
        );
    }

    if (!pkg) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3} px={3}>
                    <Card>
                        <MDBox p={4} textAlign="center">
                            <MDTypography color="error" mb={2}>
                                Package not found.
                            </MDTypography>
                            <MDButton variant="gradient" color="info" onClick={() => navigate("/trip-packages")}>
                                Back to Trip Packages
                            </MDButton>
                        </MDBox>
                    </Card>
                </MDBox>
            </DashboardLayout>
        );
    }

    const itinerary = (pkg.itineraries || [])[0];
    const days = itinerary?.itinerary_days || [];
    const linkedDestinationsLabel =
        pkg.trips && pkg.trips.length > 0
            ? pkg.trips.map((t) => t.primary_country || t.title).filter(Boolean).join(", ")
            : null;

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    {/* Main Column */}
                    <Grid item xs={12} md={8}>
                        <Card>
                            {/* Blue Header */}
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                            >
                                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                    <MDBox>
                                        <MDTypography variant="h6" color="white">
                                            Package Details: {pkg.title || "Untitled Package"}
                                        </MDTypography>
                                        <MDBox display="flex" alignItems="center" gap={1} mt={0.75} flexWrap="wrap">
                                            <StatusBadge
                                                status={pkg.status || "draft"}
                                                label={formatEnumLabel(pkg.status || "draft")}
                                            />
                                            <Chip
                                                label={formatEntityId("TP", pkg.id)}
                                                size="small"
                                                sx={headerChipSx}
                                            />
                                            {pkg.package_tier && (
                                                <Chip
                                                    label={formatEnumLabel(pkg.package_tier)}
                                                    size="small"
                                                    sx={headerChipSx}
                                                />
                                            )}
                                            {(pkg.tags || "")
                                                .split(",")
                                                .map((t) => t.trim())
                                                .filter(Boolean)
                                                .map((tag) => (
                                                    <Chip
                                                        key={tag}
                                                        label={formatEnumLabel(tag)}
                                                        size="small"
                                                        sx={headerChipSx}
                                                    />
                                                ))}
                                            {pkg.duration_days && (
                                                <Chip
                                                    label={`${pkg.duration_days} Days`}
                                                    size="small"
                                                    sx={headerChipSx}
                                                />
                                            )}
                                            {linkedDestinationsLabel && (
                                                <Chip
                                                    label={linkedDestinationsLabel}
                                                    size="small"
                                                    sx={headerChipSx}
                                                />
                                            )}
                                        </MDBox>
                                    </MDBox>

                                    <MDBox display="flex" gap={1} alignItems="center">
                                        {websiteSite && (
                                            <TextField
                                                select
                                                size="small"
                                                value={websiteSite.id}
                                                onChange={(event) => selectWebsiteSite(event.target.value)}
                                                sx={{
                                                    minWidth: 150,
                                                    "& .MuiInputBase-root": { color: "white", height: 32 },
                                                    "& .MuiSvgIcon-root": { color: "white" },
                                                    "& fieldset": { borderColor: "rgba(255,255,255,.5)" },
                                                }}
                                            >
                                                {websiteSites.map((item) => (
                                                    <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                        {websiteSite && (
                                            isWebsitePublished ? (
                                                <Tooltip title="Click to unpublish" arrow>
                                                    <Chip
                                                        label={websiteLoading ? "Updating…" : `Published to ${websiteSite.name}`}
                                                        size="small"
                                                        onClick={() => !websiteLoading && setWebsitePublished(false)}
                                                        sx={{
                                                            ...headerChipSx,
                                                            height: 32,
                                                            cursor: websiteLoading ? "default" : "pointer",
                                                            "&:hover": {
                                                                bgcolor: "rgba(255,255,255,0.28)",
                                                            },
                                                        }}
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <MDButton
                                                    size="small"
                                                    color="white"
                                                    variant="outlined"
                                                    disabled={websiteLoading || (pkg.status || "").toLowerCase() !== "active"}
                                                    onClick={() => setWebsitePublished(true)}
                                                >
                                                    {websiteLoading ? "Updating…" : `Publish to ${websiteSite.name}`}
                                                </MDButton>
                                            )
                                        )}
                                        <Tooltip title="Edit Package" arrow>
                                            <IconButton onClick={handleEditPackage} sx={commonIconButtonStyles}>
                                                <EditIcon sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={isCloning ? 'Cloning…' : 'Clone Package'} arrow>
                                                <span>
                                                    <IconButton
                                                        onClick={handleClonePackage}
                                                        disabled={isCloning}
                                                        sx={commonIconButtonStyles}
                                                    >
                                                        {isCloning
                                                            ? <CircularProgress size={18} sx={{ color: '#ffffff' }} />
                                                            : <FileCopyIcon sx={commonIconStyles} />}
                                                    </IconButton>
                                                </span>
                                        </Tooltip>
                                        <Tooltip title="Delete Package" arrow>
                                            <IconButton onClick={() => setDeleteConfirmOpen(true)} sx={commonIconButtonStyles}>
                                                <DeleteIcon sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </MDBox>

                            {/* Main Body Blocks */}
                            <MDBox p={3}>
                                {/* Package Overview */}
                                <DetailSection title="Package Overview">
                                    {isMediaLoading ? (
                                        <MDBox py={2} display="flex" justifyContent="center">
                                            <CircularProgress size={24} color="info" />
                                        </MDBox>
                                    ) : heroImageUrl ? (
                                        <Box
                                            component="img"
                                            src={heroImageUrl}
                                            alt={pkg.title || "Package hero image"}
                                            sx={{
                                                width: "100%",
                                                maxHeight: 340,
                                                objectFit: "cover",
                                                borderRadius: 2,
                                                display: "block",
                                                mb: 2.5,
                                            }}
                                        />
                                    ) : null}

                                    <MDTypography
                                        variant="body2"
                                        color="text"
                                        sx={{ pb: 3, pl: 1, borderBottom: 1, borderColor: "grey.200" }}
                                        dangerouslySetInnerHTML={{ __html: pkg.description || "No description provided." }}
                                    />
                                    <Grid container spacing={2} sx={{ pt: 2.5 }}>
                                        <Grid item xs={6} md={3}>
                                            <MDBox
                                                p={1.5}
                                                borderRadius="md"
                                                sx={{
                                                    bgcolor: "#f8f9fa",
                                                    border: "1px solid",
                                                    borderColor: "grey.200",
                                                }}
                                            >
                                                <MDTypography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                    color="text"
                                                    textTransform="uppercase"
                                                    sx={{ fontSize: "0.65rem", display: "block" }}
                                                >
                                                    Duration
                                                </MDTypography>
                                                <MDTypography variant="button" fontWeight="bold" color="dark" display="block" mt={0.5}>
                                                    {pkg.duration_days ? `${pkg.duration_days} Days` : "-"}
                                                </MDTypography>
                                            </MDBox>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <MDBox
                                                p={1.5}
                                                borderRadius="md"
                                                sx={{
                                                    bgcolor: "#f8f9fa",
                                                    border: "1px solid",
                                                    borderColor: "grey.200",
                                                }}
                                            >
                                                <MDTypography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                    color="text"
                                                    textTransform="uppercase"
                                                    sx={{ fontSize: "0.65rem", display: "block" }}
                                                >
                                                    Base Price
                                                </MDTypography>
                                                <MDTypography variant="button" fontWeight="bold" color="dark" display="block" mt={0.5}>
                                                    {formatMoney(pkg.base_price, pkg.currency)}
                                                </MDTypography>
                                            </MDBox>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <MDBox
                                                p={1.5}
                                                borderRadius="md"
                                                sx={{
                                                    bgcolor: "#f8f9fa",
                                                    border: "1px solid",
                                                    borderColor: "grey.200",
                                                }}
                                            >
                                                <MDTypography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                    color="text"
                                                    textTransform="uppercase"
                                                    sx={{ fontSize: "0.65rem", display: "block" }}
                                                >
                                                    Tier
                                                </MDTypography>
                                                <MDTypography variant="button" fontWeight="bold" color="dark" display="block" mt={0.5}>
                                                    {pkg.package_tier ? formatEnumLabel(pkg.package_tier) : "-"}
                                                </MDTypography>
                                            </MDBox>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <MDBox
                                                p={1.5}
                                                borderRadius="md"
                                                sx={{
                                                    bgcolor: "#f8f9fa",
                                                    border: "1px solid",
                                                    borderColor: "grey.200",
                                                }}
                                            >
                                                <MDTypography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                    color="text"
                                                    textTransform="uppercase"
                                                    sx={{ fontSize: "0.65rem", display: "block" }}
                                                >
                                                    Linked Trips
                                                </MDTypography>
                                                <MDTypography variant="button" fontWeight="bold" color="dark" display="block" mt={0.5}>
                                                    {(pkg.trips || []).length}
                                                </MDTypography>
                                            </MDBox>
                                        </Grid>
                                    </Grid>
                                </DetailSection>

                                {/* Gallery */}
                                <DetailSection title={`Gallery (${galleryItems.length})`} collapsible>
                                    {isMediaLoading ? (
                                        <MDBox py={3} display="flex" justifyContent="center">
                                            <CircularProgress size={24} color="info" />
                                        </MDBox>
                                    ) : galleryItems.length === 0 ? (
                                        <EmptyState
                                            message="No gallery images added yet."
                                            icon={<ImageOutlinedIcon sx={{ fontSize: 36, color: "text.secondary" }} />}
                                        />
                                    ) : (
                                        <Grid container spacing={2}>
                                            {galleryItems.map((item, idx) => (
                                                <Grid item xs={12} sm={6} md={4} key={item.id || idx}>
                                                    <Box
                                                        sx={{
                                                            border: "1px solid",
                                                            borderColor: "divider",
                                                            borderRadius: 2,
                                                            overflow: "hidden",
                                                            height: "100%",
                                                            transition: "box-shadow 0.2s",
                                                            "&:hover": { boxShadow: 3 },
                                                        }}
                                                    >
                                                        {item.image ? (
                                                            <Box
                                                                component="img"
                                                                src={item.image}
                                                                alt={item.name || "Gallery image"}
                                                                sx={{
                                                                    width: "100%",
                                                                    height: 140,
                                                                    objectFit: "cover",
                                                                    display: "block",
                                                                }}
                                                            />
                                                        ) : (
                                                            <MDBox
                                                                height={140}
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="center"
                                                                sx={{ bgcolor: "grey.100" }}
                                                            >
                                                                <ImageOutlinedIcon sx={{ color: "text.secondary" }} />
                                                            </MDBox>
                                                        )}
                                                        <MDBox p={1.5}>
                                                            <MDTypography variant="subtitle2" fontWeight="medium" noWrap>
                                                                {item.name || "Untitled"}
                                                            </MDTypography>
                                                            {item.location && (
                                                                <MDTypography variant="caption" color="text" display="block" noWrap>
                                                                    {item.location}
                                                                </MDTypography>
                                                            )}
                                                        </MDBox>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </DetailSection>

                                {/* Linked Trips */}
                                <DetailSection title={`Linked Trips (${(pkg.trips || []).length})`} collapsible>
                                    {(pkg.trips || []).length === 0 ? (
                                        <EmptyState
                                            message="Not linked to any trip yet."
                                            icon={<PlaceOutlinedIcon sx={{ fontSize: 36, color: "text.secondary" }} />}
                                        />
                                    ) : (
                                        <MDBox>
                                            {/* Header row */}
                                            <MDBox
                                                display="grid"
                                                sx={{
                                                    gridTemplateColumns: "1fr 2fr 1.2fr",
                                                    alignItems: "center",
                                                    bgcolor: "#F4F6F9",
                                                    borderRadius: "5px",
                                                    px: 2,
                                                    py: 1,
                                                    mb: 1,
                                                }}
                                            >
                                                <MDTypography variant="caption" fontWeight="bold" color="text">ID</MDTypography>
                                                <MDTypography variant="caption" fontWeight="bold" color="text">Destination</MDTypography>
                                                <MDTypography variant="caption" fontWeight="bold" color="text" textAlign="center">Status</MDTypography>
                                            </MDBox>

                                            {/* Data rows */}
                                            <MDBox display="flex" flexDirection="column" gap={1}>
                                                {pkg.trips.map((trip) => (
                                                    <MDBox
                                                        key={trip.id}
                                                        onClick={() => navigate(`/trips/detail/${trip.id}`)}
                                                        display="grid"
                                                        sx={{
                                                            gridTemplateColumns: "1fr 2fr 1.2fr",
                                                            alignItems: "center",
                                                            border: "1px solid #E8EDF5",
                                                            borderRadius: "5px",
                                                            px: 2,
                                                            py: 1.25,
                                                            bgcolor: "#F4F6F9",
                                                            cursor: "pointer",
                                                            transition: "all 0.15s ease",
                                                            "&:hover": {
                                                                borderColor: "info.main",
                                                                bgcolor: "rgba(23, 193, 232, 0.04)",
                                                            },
                                                        }}
                                                    >
                                                        <MDBox>
                                                            <MDTypography variant="button" fontWeight="bold" color="info" display="block">
                                                                {formatEntityId("TR", trip.id)}
                                                            </MDTypography>
                                                        </MDBox>

                                                        <MDBox minWidth={0}>
                                                            <MDTypography
                                                                variant="button"
                                                                fontWeight="medium"
                                                                display="block"
                                                                title={trip.title}
                                                                sx={{
                                                                    whiteSpace: "normal",
                                                                    wordBreak: "break-word",
                                                                    overflowWrap: "anywhere",
                                                                    lineHeight: 1.35,
                                                                }}
                                                            >
                                                                {trip.title || trip.primary_country || "Untitled Destination"}
                                                            </MDTypography>
                                                            {trip.primary_country && trip.title && (
                                                                <MDTypography variant="caption" color="text">
                                                                    {trip.primary_country}
                                                                </MDTypography>
                                                            )}
                                                        </MDBox>

                                                        <MDBox sx={{ justifySelf: "center" }}>
                                                            <StatusBadge
                                                                status={trip.status || "draft"}
                                                                label={formatEnumLabel(trip.status || "draft")}
                                                            />
                                                        </MDBox>
                                                    </MDBox>
                                                ))}
                                            </MDBox>
                                        </MDBox>
                                    )}
                                </DetailSection>

                                {/* Departure Dates */}
                                <DetailSection title={`Departure Dates (${(pkg.departure_dates || []).length})`} collapsible>
                                    {(pkg.departure_dates || []).length === 0 ? (
                                        <EmptyState
                                            message="No departures added yet."
                                            icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 36, color: "text.secondary" }} />}
                                        />
                                    ) : (
                                        <MDBox>
                                            {/* Header row */}
                                            <MDBox
                                                display="grid"
                                                sx={{
                                                    gridTemplateColumns: "1fr 2fr 1.5fr 1.1fr 1.2fr",
                                                    alignItems: "center",
                                                    bgcolor: "#F4F6F9",
                                                    borderRadius: "5px",
                                                    px: 2,
                                                    py: 1,
                                                    mb: 1,
                                                }}
                                            >
                                                <MDTypography variant="caption" fontWeight="bold" color="text">ID</MDTypography>
                                                <MDTypography variant="caption" fontWeight="bold" color="text">Departure City</MDTypography>
                                                <MDTypography variant="caption" fontWeight="bold" color="text">Dates</MDTypography>
                                                <MDTypography sx={{ justifySelf: "center" }} variant="caption" fontWeight="bold" color="text">Seats Left</MDTypography>
                                                <MDTypography variant="caption" fontWeight="bold" color="text" textAlign="center">Status</MDTypography>
                                            </MDBox>

                                            {/* Data rows */}
                                            <MDBox display="flex" flexDirection="column" gap={1}>
                                                {pkg.departure_dates.map((dep) => {
                                                    const seatsLeft =
                                                        dep.total_allocation != null
                                                            ? Math.max(dep.total_allocation - (dep.sold || 0), 0)
                                                            : null;

                                                    return (
                                                        <MDBox
                                                            key={dep.id}
                                                            display="grid"
                                                            sx={{
                                                                gridTemplateColumns: "1fr 2fr 1.5fr 1.1fr 1.2fr",
                                                                alignItems: "center",
                                                                border: "1px solid #E8EDF5",
                                                                borderRadius: "5px",
                                                                px: 2,
                                                                py: 1.25,
                                                                bgcolor: "#F4F6F9",
                                                                transition: "all 0.15s ease",
                                                                "&:hover": {
                                                                    borderColor: "info.main",
                                                                    bgcolor: "rgba(23, 193, 232, 0.04)",
                                                                },
                                                            }}
                                                        >
                                                            <MDBox>
                                                                <MDTypography
                                                                    variant="button"
                                                                    fontWeight="bold"
                                                                    color="info"
                                                                    display="block"
                                                                    sx={{ cursor: "pointer" }}
                                                                    onClick={() => navigate(`/trip-departure-dates/${dep.id}`)}
                                                                >
                                                                    {formatEntityId("DD", dep.id)}
                                                                </MDTypography>
                                                            </MDBox>

                                                            <MDBox>
                                                                <MDTypography variant="button" fontWeight="medium" display="block">
                                                                    {dep.departure_city || "-"}
                                                                </MDTypography>
                                                                <MDTypography variant="caption" color="text">
                                                                    Book By: {formatDate(dep.book_by_date)}
                                                                </MDTypography>
                                                            </MDBox>

                                                            <MDBox>
                                                                <MDTypography variant="button" fontWeight="medium" display="block">
                                                                    {formatDateRange(dep.departure_date, dep.return_date)}
                                                                </MDTypography>
                                                            </MDBox>

                                                            <MDBox sx={{ justifySelf: "center" }}>
                                                                <MDTypography variant="button" fontWeight="medium" display="block">
                                                                    {seatsLeft != null ? `${seatsLeft}` : "-"}
                                                                </MDTypography>
                                                            </MDBox>

                                                            <MDBox sx={{ justifySelf: "center" }}>
                                                                <StatusBadge
                                                                    status={dep.status || "draft"}
                                                                    label={formatEnumLabel(dep.status || "draft")}
                                                                />
                                                            </MDBox>
                                                        </MDBox>
                                                    );
                                                })}
                                            </MDBox>
                                        </MDBox>
                                    )}
                                </DetailSection>

                                {/* Trip Logistics */}
                                <DetailSection
                                    title={`Trip Logistics (${(pkg.components || []).length})`}
                                    collapsible
                                >
                                    {(pkg.components || []).length === 0 ? (
                                        <EmptyState
                                            message="No trip logistics added yet."
                                            icon={<Inventory2OutlinedIcon sx={{ fontSize: 36, color: "text.secondary" }} />}
                                        />
                                    ) : (
                                        <MDBox>
                                            {/* Header row */}
                                            <MDBox
                                                display="grid"
                                                sx={{
                                                    gridTemplateColumns: "1fr 2fr 1.5fr 1.5fr",
                                                    alignItems: "center",
                                                    bgcolor: "#F4F6F9",
                                                    borderRadius: "5px",
                                                    px: 2,
                                                    py: 1,
                                                    mb: 1,
                                                }}
                                            >
                                                <MDTypography variant="caption" fontWeight="bold" color="text">ID</MDTypography>
                                                <MDTypography variant="caption" fontWeight="bold" color="text">Logistic Name</MDTypography>
                                                <MDTypography variant="caption" fontWeight="bold" color="text">Category</MDTypography>
                                                <MDTypography variant="caption" fontWeight="bold" color="text">Type</MDTypography>
                                            </MDBox>

                                            {/* Data rows */}
                                            <MDBox display="flex" flexDirection="column" gap={1}>
                                                {pkg.components.map((component) => (
                                                    <MDBox
                                                        key={component.id}
                                                        display="grid"
                                                        sx={{
                                                            gridTemplateColumns: "1fr 2fr 1.5fr 1.5fr",
                                                            alignItems: "center",
                                                            border: "1px solid #E8EDF5",
                                                            borderRadius: "5px",
                                                            px: 2,
                                                            py: 1.25,
                                                            bgcolor: "#F4F6F9",
                                                            cursor: "pointer",
                                                            transition: "all 0.15s ease",
                                                            "&:hover": {
                                                                borderColor: "info.main",
                                                                bgcolor: "rgba(23, 193, 232, 0.04)",
                                                            },
                                                        }}
                                                        onClick={() => navigate(`/trip-package-components/${component.id}`)}
                                                    >
                                                        <MDBox>
                                                            <MDTypography
                                                                variant="button"
                                                                fontWeight="bold"
                                                                color="info"
                                                                display="block"
                                                            >
                                                                {formatEntityId("TL", component.id)}
                                                            </MDTypography>
                                                        </MDBox>

                                                        <MDBox minWidth={0}>
                                                            <MDTypography
                                                                variant="button"
                                                                fontWeight="medium"
                                                                display="block"
                                                                sx={{ wordBreak: "break-word" }}
                                                            >
                                                                {component.name || "Untitled Service"}
                                                            </MDTypography>
                                                        </MDBox>

                                                        <MDBox>
                                                            <MDTypography variant="caption" color="text" sx={{ textTransform: "none" }}>
                                                                {formatEnumLabel(component.category) || "-"}
                                                            </MDTypography>
                                                        </MDBox>

                                                        <MDBox>
                                                            <MDTypography variant="caption" color="text" sx={{ textTransform: "none" }}>
                                                                {formatEnumLabel(component.sub_category) || "-"}
                                                            </MDTypography>
                                                        </MDBox>
                                                    </MDBox>
                                                ))}
                                            </MDBox>
                                        </MDBox>
                                    )}
                                </DetailSection>

                                {/* Day-by-Day Itinerary */}
                                <DetailSection title="Day-by-Day Itinerary" collapsible mb={3}>
                                    {!itinerary ? (
                                        <EmptyState
                                            message="No itinerary added yet."
                                            icon={<AltRouteOutlinedIcon sx={{ fontSize: 36, color: "text.secondary" }} />}
                                        />
                                    ) : (
                                        <MDBox>
                                            {/* Itinerary info strip */}
                                            <MDBox
                                                onClick={() => navigate(`/trip-itineraries/${itinerary.id}`)}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                gap={2}
                                                sx={{
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    borderRadius: 1.5,
                                                    bgcolor: "#F4F6F9",
                                                    px: 2,
                                                    py: 1.25,
                                                    mb: 2.5,
                                                    cursor: "pointer",
                                                    transition: "all 0.15s ease",
                                                    "&:hover": { borderColor: "info.main" },
                                                }}
                                            >
                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="bold"
                                                    color="dark"
                                                    sx={{
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-word",
                                                        overflowWrap: "anywhere",
                                                        lineHeight: 1.35,
                                                    }}
                                                >
                                                    {itinerary.title || "Untitled Itinerary"}
                                                </MDTypography>
                                            </MDBox>

                                            {/* Days */}
                                            {days.length === 0 ? (
                                                <EmptyState
                                                    message="No itinerary days added yet."
                                                    icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 36, color: "text.secondary" }} />}
                                                />
                                            ) : (
                                                <MDBox display="flex" flexDirection="column" gap={2}>
                                                    {days.map((day) => (
                                                        <MDBox
                                                            key={day.id}
                                                            sx={{
                                                                border: "1px solid",
                                                                borderColor: "grey.200",
                                                                borderRadius: 1.5,
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            {/* Day header */}
                                                            <MDBox
                                                                display="flex"
                                                                alignItems="center"
                                                                gap={1.5}
                                                                sx={{
                                                                    bgcolor: "grey.50",
                                                                    borderBottom: (day.line_items || []).length > 0 ? "1px solid" : "none",
                                                                    borderColor: "grey.200",
                                                                    px: 2,
                                                                    py: 1.25,
                                                                }}
                                                            >
                                                                <MDBox
                                                                    sx={{
                                                                        width: 28,
                                                                        height: 28,
                                                                        borderRadius: "50%",
                                                                        bgcolor: "info.main",
                                                                        color: "white",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        flexShrink: 0,
                                                                    }}
                                                                >
                                                                    <MDTypography variant="caption" fontWeight="bold" color="white" sx={{ fontSize: "0.75rem" }}>
                                                                        {day.day_number}
                                                                    </MDTypography>
                                                                </MDBox>

                                                                <MDBox flex={1} minWidth={0}>
                                                                    <MDTypography
                                                                        onClick={() => navigate(`/trip-itinerary-days/${day.id}`)}
                                                                        variant="button"
                                                                        fontWeight="bold"
                                                                        color="info"
                                                                        display="block"
                                                                        sx={{ wordBreak: "break-word", cursor: "pointer" }}
                                                                    >
                                                                        {day.day_title || `Day ${day.day_number}`}
                                                                    </MDTypography>
                                                                </MDBox>
                                                            </MDBox>

                                                            {/* Line items */}
                                                            {(day.line_items || []).length > 0 && (
                                                                <MDBox sx={{ bgcolor: "white", p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                                                                    {day.line_items.map((li) => (
                                                                        <MDBox
                                                                            key={li.id}
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            gap={1.5}
                                                                            sx={{
                                                                                borderLeft: "3px solid",
                                                                                borderColor: "info.main",
                                                                                bgcolor: "grey.50",
                                                                                borderRadius: "0 4px 4px 0",
                                                                                px: 1.5,
                                                                                py: 1,
                                                                            }}
                                                                        >
                                                                            <MDBox flex={1} minWidth={0}>
                                                                                <MDTypography
                                                                                    variant="caption"
                                                                                    fontWeight="bold"
                                                                                    color="dark"
                                                                                    display="block"
                                                                                    sx={{ fontSize: "0.8rem", wordBreak: "break-word" }}
                                                                                >
                                                                                    {li.title || "Untitled item"}
                                                                                </MDTypography>
                                                                                {li.location && (
                                                                                    <MDTypography
                                                                                        variant="caption"
                                                                                        color="text"
                                                                                        display="block"
                                                                                        sx={{ fontSize: "0.72rem", mt: 0.15 }}
                                                                                    >
                                                                                        {li.location}
                                                                                    </MDTypography>
                                                                                )}
                                                                            </MDBox>

                                                                            <MDTypography
                                                                                variant="caption"
                                                                                fontWeight="bold"
                                                                                color="info"
                                                                                sx={{
                                                                                    cursor: "pointer",
                                                                                    fontSize: "0.7rem",
                                                                                    flexShrink: 0,
                                                                                }}
                                                                                onClick={() => navigate(`/trip-itinerary-day-line-items/${li.id}`)}
                                                                            >
                                                                                {formatEntityId("LI", li.id)}
                                                                            </MDTypography>
                                                                        </MDBox>
                                                                    ))}
                                                                </MDBox>
                                                            )}
                                                        </MDBox>
                                                    ))}
                                                </MDBox>
                                            )}
                                        </MDBox>
                                    )}
                                </DetailSection>

                                <DetailSection title="Audit Information" mb={0}>
                                    <TravelDetailFieldGrid
                                        data={pkg}
                                        fields={buildTravelAuditFields((userId) =>
                                            navigate(`/users/${userId}/profile`)
                                        )}
                                    />
                                </DetailSection>
                            </MDBox>
                        </Card>
                    </Grid>

                    {/* Sidebar Column */}
                    <Grid item xs={12} md={4}>
                        {/* Add-Ons Card */}
                        <Card sx={{ mb: 6 }}>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={2}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <ExtensionOutlinedIcon sx={{ color: "white !important", mr: 1 }} />
                                    <MDTypography variant="h6" color="white">
                                        Add-Ons ({(pkg.addons || []).length})
                                    </MDTypography>
                                </Box>
                            </MDBox>

                            <MDBox p={2}>
                                {(pkg.addons || []).length === 0 ? (
                                    <EmptyState
                                        message="No add-ons configured."
                                        icon={<ExtensionOutlinedIcon sx={{ fontSize: 36, color: "text.secondary" }} />}
                                    />
                                ) : (
                                    (pkg.addons || []).map((addon, index) => (
                                        <Box key={addon.id}>
                                            {index > 0 && <Divider sx={{ my: 1.5 }} />}
                                            <MDBox
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                gap={1}
                                                sx={{
                                                    cursor: "pointer",
                                                    borderRadius: 1.5,
                                                    p: 1,
                                                    mx: -0.5,
                                                    transition: "all 0.15s ease",
                                                    "&:hover": { bgcolor: "rgba(23, 193, 232, 0.06)" },
                                                }}
                                                onClick={() => navigate(`/trip-addons/${addon.id}`)}
                                            >
                                                <MDBox minWidth={0} flex={1}>
                                                    <MDTypography
                                                        variant="button"
                                                        fontWeight="medium"
                                                        color="dark"
                                                        display="block"
                                                        title={addon.name}
                                                        sx={{
                                                            whiteSpace: "normal",
                                                            wordBreak: "break-word",
                                                            overflowWrap: "anywhere",
                                                            lineHeight: 1.35,
                                                        }}
                                                    >
                                                        {addon.name || "Untitled Add-on"}
                                                    </MDTypography>
                                                    <MDTypography variant="caption" color="info" fontWeight="bold">
                                                        {formatEntityId("AD", addon.id)}
                                                    </MDTypography>
                                                </MDBox>
                                            </MDBox>
                                        </Box>
                                    ))
                                )}
                            </MDBox>
                        </Card>

                        {/* Merchant Allocations Card */}
                        <Card sx={{ mb: 6 }}>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={2}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <StorefrontOutlinedIcon sx={{ color: "white !important", mr: 1 }} />
                                    <MDTypography variant="h6" color="white">
                                        Merchant Allocations ({(pkg.merchant_allocations || []).length})
                                    </MDTypography>
                                </Box>
                            </MDBox>

                            <MDBox p={2}>
                                {(pkg.merchant_allocations || []).length === 0 ? (
                                    <EmptyState
                                        message="No merchants allocated to this package."
                                        icon={<StorefrontOutlinedIcon sx={{ fontSize: 36, color: "text.secondary" }} />}
                                    />
                                ) : (
                                    (pkg.merchant_allocations || []).map((alloc, index) => (
                                        <Box key={alloc.id || index}>
                                            {index > 0 && <Divider sx={{ my: 1.5 }} />}
                                            <MDBox
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="flex-start"
                                                gap={1}
                                                sx={{
                                                    cursor: "pointer",
                                                    borderRadius: 1.5,
                                                    p: 1,
                                                    mx: -0.5,
                                                    transition: "all 0.15s ease",
                                                    "&:hover": { bgcolor: "rgba(23, 193, 232, 0.06)" },
                                                }}
                                                onClick={() => navigate(`/merchant-allocations/${alloc.id}`)}
                                            >
                                                <MDBox minWidth={0} flex={1}>
                                                    <MDTypography
                                                        variant="button"
                                                        fontWeight="medium"
                                                        color="dark"
                                                        display="block"
                                                        title={alloc.merchant_name || alloc.name}
                                                        sx={{
                                                            whiteSpace: "normal",
                                                            wordBreak: "break-word",
                                                            overflowWrap: "anywhere",
                                                            lineHeight: 1.35,
                                                        }}
                                                    >
                                                        {alloc.merchant_name || alloc.name || "Merchant"}
                                                    </MDTypography>
                                                    <MDTypography variant="caption" color="info" fontWeight="bold" display="block">
                                                        {formatEntityId("MA", alloc.id)}
                                                    </MDTypography>
                                                    {alloc.component_name ? (
                                                        <MDTypography variant="caption" color="text" display="block">
                                                            {alloc.component_name}
                                                        </MDTypography>
                                                    ) : null}
                                                    {alloc.departure_city || alloc.departure_date ? (
                                                        <MDTypography variant="caption" color="text" display="block">
                                                            {[
                                                                alloc.departure_city,
                                                                alloc.departure_date
                                                                    ? formatDate(alloc.departure_date)
                                                                    : null,
                                                            ]
                                                                .filter(Boolean)
                                                                .join(" · ")}
                                                        </MDTypography>
                                                    ) : null}
                                                </MDBox>
                                                <StatusBadge
                                                    status={alloc.status || "Pending"}
                                                    label={formatEnumLabel(alloc.status || "Pending")}
                                                    size="xs"
                                                />
                                            </MDBox>
                                        </Box>
                                    ))
                                )}
                            </MDBox>
                        </Card>

                        {/* FAQs Card */}
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={2}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <QuizOutlinedIcon sx={{ color: "white !important", mr: 1 }} />
                                    <MDTypography variant="h6" color="white">
                                        FAQs ({(pkg.faqs || []).length})
                                    </MDTypography>
                                </Box>
                            </MDBox>

                            <MDBox p={2}>
                                {(pkg.faqs || []).length === 0 ? (
                                    <EmptyState
                                        message="No FAQs added yet."
                                        icon={<QuizOutlinedIcon sx={{ fontSize: 36, color: "text.secondary" }} />}
                                    />
                                ) : (
                                    (pkg.faqs || []).map((faq, index) => (
                                        <Box key={faq.id}>
                                            {index > 0 && <Divider sx={{ my: 1.5 }} />}
                                            <MDBox
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                gap={1}
                                                sx={{
                                                    cursor: "pointer",
                                                    borderRadius: 1.5,
                                                    p: 1,
                                                    mx: -0.5,
                                                    transition: "all 0.15s ease",
                                                    "&:hover": { bgcolor: "rgba(23, 193, 232, 0.06)" },
                                                }}
                                                onClick={() => navigate(`/trip-faqs/${faq.id}`)}
                                            >
                                                <MDBox minWidth={0} flex={1}>
                                                    <MDTypography
                                                        variant="button"
                                                        fontWeight="medium"
                                                        color="dark"
                                                        display="block"
                                                        title={faq.question}
                                                        sx={{
                                                            whiteSpace: "normal",
                                                            wordBreak: "break-word",
                                                            overflowWrap: "anywhere",
                                                            lineHeight: 1.35,
                                                        }}
                                                    >
                                                        {faq.question || "Untitled Question"}
                                                    </MDTypography>
                                                    <MDTypography variant="caption" color="info" fontWeight="bold">
                                                        {formatEntityId("FQ", faq.id)}
                                                    </MDTypography>
                                                </MDBox>
                                            </MDBox>
                                        </Box>
                                    ))
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
            <ConfirmDeleteModal
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={handleDeletePackage}
                title="Delete Package"
                message={(
                    <>
                        <MDTypography
                            variant="body1"
                            sx={{ color: "#34495e", fontSize: "16px", lineHeight: 1.6, mb: (pkg.trips || []).length ? 1.5 : 0 }}
                        >
                            {`Are you sure you want to delete "${pkg.title || "this package"}"? This action cannot be undone.`}
                        </MDTypography>
                        {(pkg.trips || []).length > 0 ? (
                            <>
                                <MDTypography variant="body2" fontWeight="medium" sx={{ color: "#34495e", mb: 1 }}>
                                    {`This package is linked to ${pkg.trips.length} destination${pkg.trips.length === 1 ? "" : "s"} and will be removed from all of them:`}
                                </MDTypography>
                                <Box component="ul" sx={{ m: 0, pl: 2.5, maxHeight: 180, overflowY: "auto" }}>
                                    {pkg.trips.map((trip) => (
                                        <Box component="li" key={trip.id} sx={{ mb: 0.5 }}>
                                            <MDTypography variant="body2" sx={{ color: "#34495e" }}>
                                                {trip.title || trip.primary_country || "Untitled Destination"}
                                            </MDTypography>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        ) : (
                            <MDTypography variant="body2" sx={{ color: "#34495e", mt: 1 }}>
                                This package isn't linked to any destination.
                            </MDTypography>
                        )}
                    </>
                )}
            />
        </DashboardLayout>
    );
};

export default TripPackageDetail;
